import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import { Alert } from '@saleshandy/design-system';
import { ChevronLeft, ChevronRight } from '@saleshandy/icons';
import { accessibleOnClick } from '../../../utils/accessible-on-click';
import { TimezoneDropdownOption } from '../types';

type IProps = {
  selectedTimezone: TimezoneDropdownOption;
  scheduleDateTime: string;
  onDateTimeChange: (datetime: string) => void;
  timeInputError: boolean;
};

const PickDateAndTime: React.FC<IProps> = ({
  scheduleDateTime,
  selectedTimezone,
  onDateTimeChange,
  timeInputError,
}) => {
  const [timePickerChange, setTimePickerChange] = useState(false);

  const scheduleDateTimeMoment = useMemo(
    () => moment.tz(scheduleDateTime, selectedTimezone.name),
    [scheduleDateTime, selectedTimezone],
  );

  useEffect(() => {
    if (!scheduleDateTime) {
      const timezoneDateTime = moment.tz(selectedTimezone.name).add(1, 'hour');
      onDateTimeChange(timezoneDateTime.toISOString());
    }
  }, [scheduleDateTime]);

  const localDate = moment(
    scheduleDateTimeMoment.format('YYYY-MM-DD'),
    'YYYY-MM-DD',
  );
  const dateForPicker = new Date(localDate.toISOString());

  const selectedTime = scheduleDateTimeMoment.format('hh:mm');
  const selectedMeridian = scheduleDateTimeMoment.format('A');
  const footerText = `${scheduleDateTimeMoment.tz()} (GMT ${scheduleDateTimeMoment.format(
    'Z',
  )})`;

  const getMonth = (date: Date): string => moment(date).format('MMMM YYYY');

  const updateScheduledDateTime = (
    date: Date,
    time: string,
    meridian: string,
  ) => {
    const timeString = `${time} ${meridian}`;
    const updatedMoment = moment.tz(
      `${moment(date).format('YYYY-MM-DD')} ${timeString}`,
      'YYYY-MM-DD hh:mm A',
      selectedTimezone.name,
    );

    onDateTimeChange(updatedMoment.toISOString());
  };

  const onDateInputChange = (date: Date) => {
    updateScheduledDateTime(date, selectedTime, selectedMeridian);
  };

  const onTimeInputChange = (time: string) => {
    updateScheduledDateTime(dateForPicker, time, selectedMeridian);
  };

  const onMeridianInputChange = (meridian: 'AM' | 'PM') => {
    updateScheduledDateTime(dateForPicker, selectedTime, meridian);
  };

  const getMeridianClassNames = (meridian: 'AM' | 'PM') =>
    classNames([
      'am-pm-text',
      {
        active: selectedMeridian === meridian,
      },
    ]);

  const customTimeInputClassName = classNames([
    'custom-time-input-filters',
    {
      'time-error': timeInputError,
    },
  ]);

  const activeBorderClassName = classNames([
    'active-border',
    {
      am: selectedMeridian === 'AM',
      pm: selectedMeridian === 'PM',
    },
  ]);

  return (
    <div className="pick-date-and-time-container">
      <div className="pick-date-and-time-calendar">
        <DatePicker
          selected={dateForPicker}
          onChange={(date: Date) => onDateInputChange(date)}
          inline
          minDate={new Date()}
          showDisabledMonthNavigation
          calendarStartDay={1}
          renderCustomHeader={({
            date,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => (
            <div className="pick-date-and-time-header">
              <p>{getMonth(date)}</p>
              <div className="pick-date-and-time-action-btns">
                <button
                  type="button"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                >
                  <ChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                >
                  <ChevronRight />
                </button>
              </div>
            </div>
          )}
        />

        <div>
          <div className="selected-date-container">
            <p>{scheduleDateTimeMoment.format('DD MMMM YYYY')}</p>
          </div>
          <div className={customTimeInputClassName}>
            <div className="hh-mm-container" key={String(timePickerChange)}>
              <TimePicker
                value={selectedTime}
                onChange={onTimeInputChange}
                onInvalidChange={() => {
                  onTimeInputChange(selectedTime);
                  setTimePickerChange(!timePickerChange); // Added this to forcefully render component for correct rendering of data
                }}
                format="hh:mm a"
                disableClock
                clearIcon={null}
              />
            </div>

            <div className="am-pm-container">
              <div
                className={getMeridianClassNames('AM')}
                {...accessibleOnClick(() => onMeridianInputChange('AM'))}
              >
                AM
              </div>
              <div
                className={getMeridianClassNames('PM')}
                {...accessibleOnClick(() => onMeridianInputChange('PM'))}
              >
                PM
              </div>
              <div className={activeBorderClassName} />
            </div>
          </div>
          {timeInputError && (
            <p className="time-error-msg">Please select valid time</p>
          )}
        </div>
      </div>

      <Alert
        className="mt-3 w-100"
        description={
          <span>
            <span className="font-semibold">Time zone:</span> {footerText}
          </span>
        }
      />
    </div>
  );
};

export default PickDateAndTime;
